import { useTheme, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { telemetryService } from "../../../services/TelemetryService";
import { format, formatDistance, parseISO } from "date-fns";

const sortEdgeById = (a, b) => b.edge_device_id - a.edge_device_id;
const DATE_FORMAT = "MM/dd/yyyy p";

const formatDate = (dateStr) => format(parseISO(dateStr), DATE_FORMAT);
const offlineDuration = (dateStr) => formatDistance(parseISO(dateStr), new Date());

const useStyles = (theme) => ({
    tableRow: {
        "&:hover": {
            backgroundColor: "#fffaec",
        },
    },
    root: {
        flexGrow: 1,
    },
    paper: {
        padding: theme.spacing(2),
        textAlign: "center",
        color: theme.palette.text.secondary,
    },
});

const DeviceTable = ({ devices, deviceType }) => {
    const theme = useTheme();
    const classes = useStyles(theme);

    if (!devices || !devices.length) {
        return <div>Loading Device List</div>;
    }

    return (
        <TableContainer component={Paper}>
            <Table size="small" aria-label="a dense table">
                <TableHead>
                    <TableRow>
                        <TableCell>Device ID</TableCell>
                        <TableCell>Serial No</TableCell>
                        <TableCell>Site Names</TableCell>
                        <TableCell>Last Report Date (Local Time)</TableCell>
                        <TableCell>Time Since</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {devices?.map((device) => (
                        <TableRow sx={classes.tableRow} key={device.edge_device_id}>
                            <TableCell component="th" scope="row">
                                <Link to={`/telemetry/device-health/${deviceType}/${device.edge_device_id}`}>
                                    {device.edge_device_id}
                                </Link>
                            </TableCell>
                            <TableCell>{device.cloudgate_serial}</TableCell>
                            <TableCell>
                                {[...new Set(device.short_name || [])]?.map((name) => (
                                    <div key={`${device.edge_device_id}:${name}`}>{name}</div>
                                ))}
                            </TableCell>
                            <TableCell>{formatDate(device.dttm_last_report)}</TableCell>
                            <TableCell>({offlineDuration(device.dttm_last_report)} ago)</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default function EdgeDeviceHealth({ deviceType }) {
    useEffect(() => {
        if (["v3", "polaris"].includes(deviceType)) {
            return;
        }

        throw new Error(`Unsupported device type "${deviceType}"`);
    }, [deviceType]);

    const [offline, setOffline] = useState([]);
    const [online, setOnline] = useState([]);
    const [noData, setNodata] = useState([]);

    const loadHealth = async () => {
        const onlineResponse = (await telemetryService.getDeviceHealth(deviceType, "OK", true))?.sort(sortEdgeById);
        setOnline(onlineResponse);

        const offlineResponse = (await telemetryService.getDeviceHealth(deviceType, "NOT OK", true))?.sort(
            sortEdgeById
        );
        setOffline(offlineResponse);

        const noDataDevices = (await telemetryService.getDeviceHealth(deviceType, "NO DATA", true))?.sort(sortEdgeById);
        setNodata(noDataDevices);
    };

    useEffect(() => loadHealth(), []);

    return (
        <>
            <h3>{deviceType} Offline Devices</h3>
            <DeviceTable devices={offline} deviceType={deviceType} />

            <h3>{deviceType} No Data Devices</h3>
            <DeviceTable devices={noData} deviceType={deviceType} />

            <h3>{deviceType} Healthy Devices</h3>
            <DeviceTable devices={online} deviceType={deviceType} />
        </>
    );
}
